import React from 'react';
import { Button, InputNumber, Space } from 'antd';
import { computeDiscount, DiscountMode } from '../../utils/discountMath';
import { formatCurrency } from '../../utils/formatCurrency';

export interface DiscountValue {
  mode: DiscountMode;
  percentage?: number | null;
  amount?: number | null;
  finalPrice?: number | null;
}

export interface DiscountToggleProps {
  base: number;                 // gross, for the live net preview
  value: DiscountValue;
  onChange: (v: DiscountValue) => void;
  round?: boolean;              // parts=true
  size?: 'small' | 'middle';
  disabled?: boolean;
}

/** Shared 3-way discount control (%/$/Final-price button group + matching input)
 *  used by both the estimate editor and the WO inline editor. Selecting a mode
 *  clears the other two value fields (mirrors ServiceItemsTable's sibling-zeroing
 *  behavior) and a live "Net: $X" preview is computed via `computeDiscount`. */
const DiscountToggle: React.FC<DiscountToggleProps> = ({ base, value, onChange, round, size = 'small', disabled }) => {
  const { mode } = value;

  const selectMode = (next: DiscountMode) => {
    if (next === 'percentage') {
      onChange({ ...value, mode: 'percentage', amount: null, finalPrice: null });
    } else if (next === 'amount') {
      onChange({ ...value, mode: 'amount', percentage: null, finalPrice: null });
    } else {
      onChange({ ...value, mode: 'final_price', percentage: null, amount: null });
    }
  };

  const { net } = computeDiscount({
    base,
    mode,
    percentage: value.percentage,
    amount: value.amount,
    finalPrice: value.finalPrice,
    round,
  });

  const btnStyle: React.CSSProperties = { height: 22, fontSize: 11, padding: '0 6px', borderRadius: 4 };

  return (
    <div>
      <Space size={4}>
        <Button size="small" disabled={disabled} type={mode === 'percentage' ? 'primary' : 'default'} style={btnStyle} onClick={() => selectMode('percentage')}>%</Button>
        <Button size="small" disabled={disabled} type={mode === 'amount' ? 'primary' : 'default'} style={btnStyle} onClick={() => selectMode('amount')}>$</Button>
        <Button size="small" disabled={disabled} type={mode === 'final_price' ? 'primary' : 'default'} style={btnStyle} onClick={() => selectMode('final_price')}>Final</Button>
      </Space>
      <div style={{ marginTop: 6 }}>
        {mode === 'percentage' ? (
          <InputNumber
            min={0} max={100} step={1} style={{ width: '100%' }} size={size} placeholder="0" suffix="%" controls={false} disabled={disabled}
            value={value.percentage ?? undefined}
            onChange={(v) => onChange({ ...value, mode: 'percentage', percentage: v ?? null, amount: null, finalPrice: null })}
          />
        ) : mode === 'final_price' ? (
          <InputNumber
            min={0} step={round ? 0.01 : 1} style={{ width: '100%' }} size={size} placeholder="Final price" prefix="$" controls={false} disabled={disabled}
            value={value.finalPrice ?? undefined}
            onChange={(v) => onChange({ ...value, mode: 'final_price', finalPrice: v ?? null, percentage: null, amount: null })}
          />
        ) : (
          <InputNumber
            min={0} step={1} style={{ width: '100%' }} size={size} placeholder="0.00" prefix="$" controls={false} disabled={disabled}
            value={value.amount ?? undefined}
            onChange={(v) => onChange({ ...value, mode: 'amount', amount: v ?? null, percentage: null, finalPrice: null })}
          />
        )}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: '#10b981', fontWeight: 600 }}>
        Net: {formatCurrency(net)}
      </div>
    </div>
  );
};

export default DiscountToggle;
